import { Button, Flex, Form, Modal, Spin } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { type FC, useEffect, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import type { AiAgentsFormValues } from './lib/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormSchema } from './hooks/use-form-schema';
import { useTranslation } from 'react-i18next';
import styles from './createa-ai-agent-modal.module.scss';
import { AI_AGENTS_MAX_FORM_LIMIT } from './lib/constants';
import classNames from 'classnames';
import { Text } from '@shared/ui/antd';

import { useCreateAgent } from '@shared/hooks/use-create-ai-agent';
import { useUpdateAgent } from '@shared/hooks/use-update-ai-agent';
import { useGetAgentInfoAgentInfoGetQuery } from '@shared/api';
import { Routes } from '@shared/constants/routes';

type AgentModalMode = 'create' | 'edit';

interface CreateAiAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: AgentModalMode;
  agentId?: number | null;
  shouldNavigateToCreatedAgent?: boolean;
}

export const CreateAiAgentModal: FC<CreateAiAgentModalProps> = ({
  isOpen,
  onClose,
  mode = 'create',
  agentId = null,
  shouldNavigateToCreatedAgent = true,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'aiAgents.modal' });
  const formSchema = useFormSchema();
  const navigate = useNavigate();

  const isEdit = mode === 'edit' && typeof agentId === 'number';

  const { createAgent, isLoading: isCreating } = useCreateAgent();
  const { updateAgent, isLoading: isUpdating } = useUpdateAgent();

  const {
    data: agentInfo,
    isFetching: isAgentInfoLoading,
    isError: isAgentInfoError,
  } = useGetAgentInfoAgentInfoGetQuery(
    { agentId: agentId as number },
    { skip: !isOpen || !isEdit },
  );

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<AiAgentsFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  const initialName = useMemo(() => {
    if (!isEdit) return '';
    return agentInfo?.name ?? '';
  }, [isEdit, agentInfo?.name]);

  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit) {
      reset({ name: '' });
      return;
    }

    if (agentInfo) {
      reset({ name: agentInfo.name ?? '' });
    }
  }, [isOpen, isEdit, agentInfo, reset]);

  const isFormDisabled = isSubmitting || isCreating || isUpdating || (isEdit && isAgentInfoLoading);

  const handleSubmitForm = async (values: AiAgentsFormValues) => {
    const payload = {
      name: values.name,
      role: isEdit ? (agentInfo?.role ?? '') : '',
      style: isEdit ? (agentInfo?.style ?? '') : '',
      rules: isEdit ? (agentInfo?.rules ?? []) : [],
      exceptions: isEdit ? (agentInfo?.exceptions ?? []) : [],
    };

    if (isEdit) {
      if (!agentId) return;
      await updateAgent(agentId, payload);
    } else {
      const createdAgent = await createAgent(payload);
      if (createdAgent?.id && shouldNavigateToCreatedAgent) {
        navigate(
          generatePath(Routes.aiAgents.agent, {
            agentId: createdAgent.id,
          }),
        );
      }
    }

    reset({ name: '' });
    onClose();
  };

  const handleCancel = () => {
    reset({ name: isEdit ? initialName : '' });
    onClose();
  };

  const title = isEdit ? t('editTitle') : t('createTitle');
  const submitLabel = isEdit ? t('editConfirm') : t('createConfirm');

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={handleCancel}
      destroyOnClose
      width="auto"
      style={{ background: 'var(--color-bg-muted)', borderRadius: '12px' }}
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ hidden: true }}
    >
      <Flex vertical gap={20}>
        <Text variant="3xl" fontWeight={700} style={{ textAlign: 'start' }}>
          {title}
        </Text>

        {isEdit && (isAgentInfoLoading || isAgentInfoError) ? (
          <Flex align="center" justify="center" style={{ padding: 24 }}>
            {isAgentInfoLoading ? <Spin /> : <Text>{t('loadError')}</Text>}
          </Flex>
        ) : (
          <Form
            onChange={() => clearErrors('root')}
            onFinish={handleSubmit(handleSubmitForm)}
            className={styles.root}
            layout="vertical"
          >
            <Form.Item
              name="name"
              label={t('name')}
              help={errors.name && errors.name.message}
              validateStatus={errors.name ? 'error' : ''}
              className={styles.label}
            >
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextArea
                    {...field}
                    disabled={isFormDisabled}
                    id="name"
                    placeholder={t('namePlaceholder')}
                    className={styles.area}
                    style={{ resize: 'none' }}
                    showCount
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    maxLength={AI_AGENTS_MAX_FORM_LIMIT.name}
                  />
                )}
              />
            </Form.Item>

            <Flex gap="12px" align="center" justify="end" className={styles.button_container}>
              <Button
                variant="text"
                onClick={handleCancel}
                className={classNames(styles.button, styles.button_cancel)}
                disabled={isFormDisabled}
              >
                {t('cancel')}
              </Button>

              <Form.Item className={styles.button}>
                <Button
                  className={styles.button}
                  block
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting || isCreating || isUpdating}
                  disabled={
                    isFormDisabled || !isValid || (!isEdit && !isDirty) || (isEdit && !isDirty)
                  }
                >
                  {submitLabel}
                </Button>
              </Form.Item>
            </Flex>
          </Form>
        )}
      </Flex>
    </Modal>
  );
};
